import  { useState, useEffect } from 'react';
import { 
  Box, Grid, TextField, 
  FormControl, InputLabel, Select, MenuItem, 
  Button, Stack, Chip, Typography
} from '@mui/material';
import { Search, Clear, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const UserFilter = ({ filters, roles, onFilterChange, loading = false }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || '');

  const phaseNorm = (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
  const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, '');
  const visibleRoles = Array.isArray(roles)
    ? roles.filter((r) => {
        const nm = norm(r?.name);
        if (phaseNorm === 'sec' && nm === 'headmaster') return false;
        if (phaseNorm === 'nur_pri' && nm === 'principal') return false;
        return true;
      })
    : roles;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Update parent when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFilterChange('search', debouncedSearch);
    }
  }, [debouncedSearch, filters.search, onFilterChange]);

  const handleChange = (name, value) => {
    onFilterChange(name, value);
  };

  const handleReset = () => {
    setSearchValue('');
    onFilterChange('role', '');
    onFilterChange('status', '');
    onFilterChange('search', '');
  };

  const handleRefresh = () => {
    // Trigger a refresh of the data
    onFilterChange('refresh', Date.now());
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.role) count++;
    if (filters.status) count++;
    if (filters.search) count++;
    return count;
  };

  return (
    <Box component="form">
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('users.filter.searchLabel') || t('common.search') || 'Search Users'}
            placeholder={t('users.filter.searchPlaceholder') || 'Search by name, email, or role...'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: <Search fontSize="small" sx={{ mr: 1 }} />
            }}
            helperText={t('users.filter.searchHelper') || 'Search by name, email, or role'}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>{t('users.filter.roleLabel') || t('users.form.role') || 'Role'}</InputLabel>
            <Select
              value={filters.role}
              onChange={(e) => handleChange('role', e.target.value)}
              label={t('users.filter.roleLabel') || t('users.form.role') || 'Role'}
              disabled={loading}
            >
              <MenuItem value="">{t('users.filter.allRoles') || t('common.all') || 'All Roles'}</MenuItem>
              {visibleRoles && visibleRoles.length > 0 ? (
                visibleRoles.map(role => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {visibleRoles === null 
                    ? (t('users.filter.loadingRoles') || 'Loading roles...') 
                    : (t('users.filter.noRoles', { count: visibleRoles?.length || 0 }) || `No roles found (${visibleRoles?.length || 0})`)
                  }
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>{t('users.filter.statusLabel') || t('users.form.status') || 'Status'}</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleChange('status', e.target.value)}
              label={t('users.filter.statusLabel') || t('users.form.status') || 'Status'}
              disabled={loading}
            >
              <MenuItem value="">{t('users.filter.allStatuses') || 'All Statuses'}</MenuItem>
              <MenuItem value="active">{t('status.active') || 'Active'}</MenuItem>
              <MenuItem value="inactive">{t('status.inactive') || 'Inactive'}</MenuItem>
              <MenuItem value="suspended">{t('status.suspended') || 'Suspended'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Button 
              variant="outlined" 
              startIcon={<Refresh />}
              onClick={handleRefresh}
              title={t('users.filter.refreshTitle') || 'Refresh data'}
              size="medium"
              sx={{ minWidth: 100 }}
              disabled={loading}
            >
              {t('users.filter.refresh') || t('common.refresh') || 'Refresh'}
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<Clear />}
              onClick={handleReset}
              title={t('users.filter.resetTitle') || 'Clear all filters'}
              size="medium"
              sx={{ minWidth: 90 }}
              disabled={loading}
            >
              {t('users.filter.reset') || 'Reset'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {getActiveFiltersCount() > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('users.filter.activeFilters') || 'Active Filters:'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filters.role && (
              <Chip 
                label={
                  t('users.filter.roleChip', { 
                    role: roles.find(r => r.id === parseInt(filters.role))?.name || filters.role 
                  }) || `Role: ${roles.find(r => r.id === parseInt(filters.role))?.name || filters.role}`
                }
                onDelete={() => handleChange('role', '')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.status && (
              <Chip 
                label={t('users.filter.statusChip', { status: filters.status }) || `Status: ${filters.status}`}
                onDelete={() => handleChange('status', '')}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {filters.search && (
              <Chip 
                label={t('users.filter.searchChip', { query: filters.search }) || `Search: "${filters.search}"`}
                onDelete={() => setSearchValue('')}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
      )}

      {/* Roles Info */}
      {roles && roles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('users.filter.rolesInfo', { count: (Array.isArray(visibleRoles) ? visibleRoles.length : roles.length) }) || `Available Roles: ${(Array.isArray(visibleRoles) ? visibleRoles.length : roles.length)} roles loaded`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UserFilter;